import React, { useRef } from 'react';

import {
  Box,
  Text,
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  RadioGroup,
  Radio,
  Stack,
  Select,
  Textarea,
  HStack,
  Badge,
  Button,
  Icon,
} from '@chakra-ui/react';
import { FaFileCsv, FaUpload, FaTimes } from 'react-icons/fa';

import { useColors } from './colors';
import { getEntityId, getEntityName } from '../utils/entityOptions.util';

const PartslinkScopeSection = ({
  scopeMode,
  onScopeModeChange,
  partslinkCsvFile,
  onPartslinkCsvFileChange,
  manualPartslinks,
  onManualPartslinksChange,
  selectedStoreId,
  onStoreChange,
  storeOptions = [],
}) => {
  const { textColor, backgroundCardColor, borderColor, secondaryTextColor, inputColor, mutedBg } =
    useColors();
  const inputRef = useRef(null);

  const manualPartslinkList = manualPartslinks
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const handleCsvFile = (file) => {
    if (!file) return;
    onPartslinkCsvFileChange(file);
  };

  const clearCsvFile = () => {
    onPartslinkCsvFileChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Box
      p={5}
      bg={backgroundCardColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
      height="100%"
    >
      <Text color={textColor} fontSize="md" fontWeight="semibold" mb={1}>
        Partslink scope
      </Text>
      <Text color={secondaryTextColor} fontSize="sm" mb={4}>
        Choose which partslinks to create listings for. Each partslink is resolved to a supplier SKU
        via the mapping file, then used to find and compile reference data from the verified seller.
      </Text>

      <RadioGroup value={scopeMode} onChange={onScopeModeChange}>
        <Stack spacing={3}>
          <Radio value="csv" colorScheme="teal">
            <Text fontSize="sm" color={textColor}>
              Partslink CSV file
            </Text>
          </Radio>
          <Radio value="manual" colorScheme="teal">
            <Text fontSize="sm" color={textColor}>
              Manual partslink list
            </Text>
          </Radio>
        </Stack>
      </RadioGroup>

      {scopeMode === 'csv' && (
        <VStack align="stretch" spacing={3} mt={4}>
          <Text color={secondaryTextColor} fontSize="sm">
            Upload a CSV with a single column: <strong>partslink</strong> (header required). One
            partslink per row — used as the create list for this run.
          </Text>

          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => handleCsvFile(e.target.files?.[0])}
          />

          {!partslinkCsvFile ? (
            <Box
              p={8}
              borderWidth="2px"
              borderStyle="dashed"
              borderColor={borderColor}
              borderRadius="lg"
              bg={mutedBg}
              textAlign="center"
            >
              <Icon as={FaFileCsv} boxSize={10} color="teal.500" mb={3} />
              <Text color={textColor} fontWeight="medium" mb={2}>
                Drop partslink CSV here or browse
              </Text>
              <Text color={secondaryTextColor} fontSize="sm" mb={4}>
                Required column: partslink
              </Text>
              <Button
                leftIcon={<FaUpload />}
                colorScheme="teal"
                size="sm"
                onClick={() => inputRef.current?.click()}
              >
                Choose file
              </Button>
            </Box>
          ) : (
            <HStack
              p={3}
              bg={mutedBg}
              borderRadius="md"
              justify="space-between"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <HStack align="flex-start">
                <Icon as={FaFileCsv} color="teal.500" mt={1} />
                <Box>
                  <Text color={textColor} fontSize="sm" fontWeight="medium">
                    {partslinkCsvFile.name}
                  </Text>
                  <Text color={secondaryTextColor} fontSize="xs">
                    {(partslinkCsvFile.size / 1024).toFixed(1)} KB · Parsed on start
                  </Text>
                </Box>
              </HStack>
              <Button size="sm" variant="ghost" leftIcon={<FaTimes />} onClick={clearCsvFile}>
                Remove
              </Button>
            </HStack>
          )}

          <HStack spacing={2}>
            <Badge colorScheme="gray" variant="subtle">
              .csv
            </Badge>
            <Badge colorScheme="gray" variant="subtle">
              partslink
            </Badge>
          </HStack>
        </VStack>
      )}

      {scopeMode === 'manual' && (
        <VStack align="stretch" spacing={3} mt={4}>
          <FormControl>
            <FormLabel color={textColor} fontSize="sm">
              Partslinks (one per line)
            </FormLabel>
            <Textarea
              value={manualPartslinks}
              onChange={(e) => onManualPartslinksChange(e.target.value)}
              placeholder={'HO123456\nFO456789\nGM789012'}
              rows={8}
              fontFamily="mono"
              fontSize="sm"
              color={inputColor}
            />
            <FormHelperText>
              Enter partslink numbers exactly as they appear in your mapping file.
            </FormHelperText>
          </FormControl>
          {manualPartslinkList.length > 0 && (
            <HStack>
              <Badge colorScheme="teal" variant="subtle">
                {manualPartslinkList.length} partslink{manualPartslinkList.length === 1 ? '' : 's'}{' '}
                in list
              </Badge>
            </HStack>
          )}
        </VStack>
      )}

      <FormControl mt={6}>
        <FormLabel color={textColor} fontSize="sm">
          Store
        </FormLabel>
        <Select
          placeholder="Select store"
          value={selectedStoreId}
          onChange={(e) => onStoreChange(e.target.value)}
          color={inputColor}
          size="sm"
        >
          {storeOptions.map((store) => (
            <option key={getEntityId(store)} value={getEntityId(store)}>
              {getEntityName(store)}
            </option>
          ))}
        </Select>
        <FormHelperText>
          Choose the store for eBay API access and publishing created listings. Default fulfillment,
          payment, and return policies from the store policies tab are applied to each new listing.
        </FormHelperText>
      </FormControl>
    </Box>
  );
};

export default PartslinkScopeSection;
